import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { createSelector } from 'reselect';

import exporter from '../exporter';
import {
  onFetchSuccess as defaultOnFetchSuccess,
  onFetchError,
  onFetchStart,
  apiCall,
  fetchInitialArrayState,
} from '../utils';
import * as api from '../../api/common';
import { actionTypes, createAction } from '../actions';
import moment from '../../utils/moment';
import { PayloadAction } from '../../types/state';
import { Category, Prestation, Subcategory, Universe } from '../../types/commons';

import { UniversesState } from './types';

// ~~~~~~~~~~ CONSTANTS ~~~~~~~~~~

export const FETCH = actionTypes('UNIVERSES_FETCH');
export const SET = 'UNIVERSES_SET';

// ~~~~~~~~~~ ACTIONS ~~~~~~~~~~

export const fetch = () => apiCall(FETCH, api.universes.get);

export const set = (universes: Universe[]) => createAction(SET, universes);

// ~~~~~~~~~~ SELECTORS ~~~~~~~~~~
export const getUniversesSelector = ({ universes }: { universes: UniversesState }) => universes.all;

export const getAllPrestationsSelector = createSelector([getUniversesSelector], (universes): Prestation[] =>
  universes.flatMap((universe: Universe) =>
    universe.categories.flatMap((category: Category) =>
      category.subcategories.flatMap((subcategory: Subcategory) => subcategory.prestations),
    ),
  ),
);

export const getAllSpotlightPrestationsSelector = createSelector(
  [getAllPrestationsSelector],
  (allPrestations): Prestation[] => {
    const allSpotlightPrestations = allPrestations
      // For now, the list will be the same whatever the country or zone.
      // That's why we're using ['1'] for now.
      .filter((prestation: Prestation) => prestation.spotlight['1'] === true);
    const allSpotlightPrestationsSortBy = sortBy(allSpotlightPrestations, ['spotlight_position[1]']);
    // One service can sometimes be found in 2 different universes.
    // For example, massage can be found in the beauty and massage universe.
    // We need to sort by reference to avoid duplicates.
    return uniqBy(allSpotlightPrestationsSortBy, 'reference');
  },
);

// ~~~~~~~~~~ REDUCER ~~~~~~~~~~
export const initialState: UniversesState = {
  ...fetchInitialArrayState,
  fetchedAt: undefined,
};

const onFetchSuccess = (state: UniversesState, action: PayloadAction<Universe[]>) => ({
  ...defaultOnFetchSuccess(state, action),
  fetchedAt: moment(),
});

const onSet = (state: UniversesState, action: PayloadAction<Universe[]>) => ({
  ...state,
  isLoading: false,
  isLoaded: true,
  all: action.payload,
});

const handlers = {
  [FETCH.START]: onFetchStart,
  [FETCH.SUCCESS]: onFetchSuccess,
  [FETCH.ERROR]: onFetchError,
  [SET]: onSet,
};

export const reducer = exporter<UniversesState>(initialState, handlers);
